import { useEffect, useState } from "react";
import { useHlsVideo } from "hooks/useHlsVideo";
import { useVideoAspectRatio } from "hooks/useVideoAspectRatio";
import { useSynchronizedVideo } from "../hooks/useSynchronizedVideo";
import {
  useVideoControlActions,
  useVideoControlState,
} from "../contexts/VideoControlContext";
import { useVideoSuspended } from "../contexts/VideoSuspensionContext";
import { useDocumentHiddenFor } from "../hooks/useDocumentHiddenFor";
import {
  DEFAULT_VIDEO_ASPECT_RATIO,
  HIDDEN_TAB_SUSPEND_DELAY_MS,
  LOW_MEMORY_VIDEO_BUFFER_BUDGET,
  NORMAL_VIDEO_BUFFER_BUDGET,
} from "../constants";
import { useLowMemoryVideoMode, useVideoQuality } from "hooks/settings";
import { HlsVideoProps } from "hooks/useHlsVideo";
import { ReconnectingOverlay } from "components/scrapEyeVideoPlayer/ReconnectingOverlay";
import { Alert, CircularProgress } from "@mui/material";

interface SynchronizedHlsVideoProps extends HlsVideoProps {
  videoId: string;
  videoIndex: number;
  expectedStartIso?: string;
}

export const SynchronizedHlsVideo = ({
  videoId,
  videoIndex,
  expectedStartIso,
  srcUrl,
  isLive = false,
  autoPlay,
  muted = true,
  style,
  className,
  onTokenMissing,
  onTokenError,
  positionSeconds,
}: SynchronizedHlsVideoProps) => {
  const actions = useVideoControlActions();
  const {
    playbackRate: sharedPlaybackRate,
    isPlaying,
    isBuffering: sharedBuffering,
  } = useVideoControlState();
  // Live tiles ignore the shared VOD playback rate — at 16x (the default
  // for first-ever load or a historical session persisted in localStorage)
  // the live buffer drains faster than it fills, producing the exact
  // stall → watchdog-startLoad → stall loop this PR is fixing.
  const playbackRate = isLive ? 1 : sharedPlaybackRate;
  const [shouldInitialize, setShouldInitialize] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isSuspendedByPage = useVideoSuspended();
  const isTabHidden = useDocumentHiddenFor(HIDDEN_TAB_SUSPEND_DELAY_MS);
  const isSuspended = isSuspendedByPage || isTabHidden;
  const lowMemoryVideoMode = useLowMemoryVideoMode();
  const videoQuality = useVideoQuality();

  // Blanking the url tears the hls.js instance down and frees its buffered segments;
  // restoring it rebuilds the tile and re-registers it with the shared clock. The same
  // delay re-arms on resume, so a grid does not request every manifest in one render.
  useEffect(() => {
    if (isSuspended) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- releases the buffer as the page suspends
      setIsStreaming(false);
      return;
    }
    const timer = setTimeout(() => {
      setShouldInitialize(true);
      setIsStreaming(true);
    }, videoIndex * 100);
    return () => {
      clearTimeout(timer);
    };
  }, [videoIndex, isSuspended]);

  const {
    ref: videoRef,
    fatalError,
    isReconnecting,
    getForwardBufferEnd,
    trackSeek,
    levelAspectRatio,
  } = useHlsVideo({
    srcUrl: isStreaming ? srcUrl : "",
    isLive,
    playbackRate,
    quality: videoQuality,
    bufferBudget: lowMemoryVideoMode
      ? LOW_MEMORY_VIDEO_BUFFER_BUDGET
      : NORMAL_VIDEO_BUFFER_BUDGET,
    // Live streams auto-play by default; explicit prop overrides. The shared
    // logical clock is not meaningful for them, so we skip the
    // VideoControlContext gating used for historical playback.
    autoPlay: autoPlay ?? isLive,
    muted,
    style,
    className,
    onTokenMissing,
    onTokenError,
    onPlaylistStart: expectedStartIso
      ? (startTimeMs) => {
          const expectedMs = new Date(expectedStartIso).getTime();
          const offsetSeconds = (expectedMs - startTimeMs) / 1000;
          if (offsetSeconds > 0.5) {
            actions.setVideoStartOffset(videoId, offsetSeconds);
          }
        }
      : undefined,
    positionSeconds,
    playbackContext: isLive
      ? undefined
      : {
          state: !isPlaying
            ? "paused"
            : sharedBuffering
              ? "buffering"
              : "playing",
          report: actions.playbackReport,
          takeHoldCauseMs: () => actions.takeHoldCauseMs(videoId),
        },
  });

  // Track when video has loaded enough data to display
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldInitialize) return;

    const handleLoadedData = () => {
      setIsInitialLoading(false);
    };

    video.addEventListener("loadeddata", handleLoadedData);

    // If video already has data, update immediately
    if (video.readyState >= 2) {
      setIsInitialLoading(false);
    }

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
    };
  }, [videoRef, shouldInitialize]);

  // Reset loading state when srcUrl changes
  useEffect(() => {
    if (isStreaming && srcUrl) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets loading flag when video source URL changes
      setIsInitialLoading(true);
    }
  }, [srcUrl, isStreaming]);

  const { isBuffering } = useSynchronizedVideo({
    videoRef,
    videoId,
    videoIndex,
    enabled: isStreaming,
    isLive,
    getForwardBufferEnd,
    onSeek: trackSeek,
  });

  const decodedAspectRatio = useVideoAspectRatio(videoRef);
  const aspectRatio = levelAspectRatio ?? decodedAspectRatio;

  // Live monitoring grids must not pause every feed when a single tile is
  // clicked, so disable the global play/pause toggle in live mode.
  const handleVideoClick = isLive
    ? undefined
    : () => {
        actions.togglePlayPause();
      };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: aspectRatio ?? DEFAULT_VIDEO_ASPECT_RATIO,
        background: "black",
        borderRadius: 8,
        overflow: "hidden",
        cursor: isLive ? "default" : "pointer",
        ...style,
      }}
      className={className}
      onClick={handleVideoClick}
    >
      {!shouldInitialize ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </div>
      ) : fatalError ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {fatalError}
        </Alert>
      ) : (
        <>
          <video
            data-video-id={videoId}
            ref={videoRef}
            style={{ width: "100%", height: "100%", objectFit: "fill" }}
            playsInline
            muted={muted}
          />
          {isInitialLoading || (!isLive && isBuffering) ? (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <CircularProgress />
            </div>
          ) : null}
          {isReconnecting ? <ReconnectingOverlay /> : null}
        </>
      )}
    </div>
  );
};
